import { renderFleetMap, destroyFleetMap } from './pages/fleet-map';
import { renderShipments } from './pages/shipments';
import { renderShipmentDetail } from './pages/shipment-detail';
import { renderAlerts } from './pages/alerts';
import { renderVehicles, renderVehicleDetail } from './pages/vehicles';
import { initNotifications, updateNavBadge } from './notifications';
import {
  createShell, createRouter, navigate,
  statusBadge, severityBadge, timeAgo,
  injectStyles, THEME_CSS, BASE_CSS, LEAFLET_CSS,
  initTheme, icon,
} from '@demo/ui';
import type { Route } from '@demo/ui';

export { navigate, statusBadge, severityBadge, timeAgo };

export function eventTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    temp_excursion: 'Temperature Excursion',
    door_open: 'Door Opened',
    gps_lost: 'GPS Signal Lost',
    route_deviation: 'Route Deviation',
    delivery_arrival: 'Delivery Arrival',
  };
  return labels[type] || type;
}

export function eventTypeIcon(type: string): string {
  const icons: Record<string, string> = {
    temp_excursion: `<svg class="w-5 h-5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-2.99L13.73 4.01c-.77-1.33-2.69-1.33-3.46 0L3.34 16.01C2.57 17.33 3.53 19 5.07 19z"/></svg>`,
    door_open: `<svg class="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z"/></svg>`,
    gps_lost: `<svg class="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>`,
    route_deviation: `<svg class="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"/></svg>`,
    delivery_arrival: `<svg class="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>`,
  };
  return icons[type] || icons.temp_excursion;
}

function mapIcon() {
  return `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"/></svg>`;
}
function shipmentIcon() {
  return `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg>`;
}
function alertIcon() {
  return `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/></svg>`;
}
function vehicleIcon() {
  return `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 17h8M8 17a2 2 0 01-2-2V9.5a.5.5 0 01.5-.5h1.379a1 1 0 00.894-.553l.894-1.789A1 1 0 0110.561 6h2.878a1 1 0 01.894.553l.894 1.789a1 1 0 00.894.553H17.5a.5.5 0 01.5.5V15a2 2 0 01-2 2M8 17a2 2 0 002 2h4a2 2 0 002-2"/></svg>`;
}

function getActiveNav(hash: string): string {
  if (hash.startsWith('#/shipments')) return 'shipments';
  if (hash.startsWith('#/alerts')) return 'alerts';
  if (hash.startsWith('#/vehicles')) return 'vehicles';
  return 'map';
}

document.addEventListener('DOMContentLoaded', () => {
  initTheme();
  injectStyles(THEME_CSS, BASE_CSS, LEAFLET_CSS);

  const shell = createShell({
    brand: {
      name: 'FrostTrack',
      subtitle: 'Cold Chain Monitor',
      icon: '<svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"/></svg>',
    },
    navItems: [
      { id: 'map', label: 'Fleet Map', icon: mapIcon(), hash: '#/' },
      { id: 'shipments', label: 'Shipments', icon: shipmentIcon(), hash: '#/shipments' },
      { id: 'alerts', label: 'Alerts', icon: alertIcon(), hash: '#/alerts', hasBadge: true },
      { id: 'vehicles', label: 'Vehicles', icon: vehicleIcon(), hash: '#/vehicles' },
    ],
  });

  const routes: Route[] = [
    { pattern: '#/', render: (c) => renderFleetMap(c) },
    { pattern: '#/shipments', render: (c) => renderShipments(c) },
    { pattern: '#/shipments/:id', render: (c, p) => renderShipmentDetail(c, p.id) },
    { pattern: '#/alerts', render: (c) => renderAlerts(c) },
    { pattern: '#/vehicles', render: (c) => renderVehicles(c) },
    { pattern: '#/vehicles/:id', render: (c, p) => renderVehicleDetail(c, p.id) },
  ];

  createRouter(routes, shell, getActiveNav);
  initNotifications();
  updateNavBadge();

  (window as any).__shell = shell;
});
